import { z } from "zod"

export const BasketAttributesSchema = z.object({
  survey_token: z.string().nullable(),
})

export const CompanyResponseSchema = z.object({
  address: z.string(),
  street_name: z.string(),
  street_number: z.string(),
  apartment_number: z.string(),
  city: z.string(),
  company_name: z.string(),
  country: z.string(),
  marketing_agreement: z.boolean(),
  personal_data_processing: z.boolean(),
  email: z.string(),
  first_name: z.string(),
  postal_code: z.string(),
  tax: z.array(
    z.object({
      eu_id: z.string(),
      type: z.enum(["vat", "regon"]),
      country_code: z.string(),
      id: z.string(),
    })
  ),
  type: z.string(),
})

export const UserRegistrationResponseSchema = z.object({
  signature: z.string(),
  hitId: z.string().nullable(),
  accountId: z.string().nullable(),
  messages: z.array(
    z.object({
      class: z.string().optional(),
      type: z.string(),
      message: z.string(),
      id: z.string(),
    })
  ),
})

export type UserRegistrationResponse = z.infer<
  typeof UserRegistrationResponseSchema
>

export type CompanyResponse = z.infer<typeof CompanyResponseSchema>

export const CloudBluePeriodValueSchema = z.object({
  duration: z.number().nullable(),
  unit: z.union([z.enum(["MONTHS", "YEARS", "DAYS"]), z.string()]).nullable(),
})

export const BasePriceShapeSchema = z.object({
  currency_symbol: z.string(),
  currency_code: z.string(),
  tax_formatted: z.string(),
  net_value: z.string(),
  net_value_cents: z.string(),
  net_value_decimals: z.string(),
  net_value_formatted: z.string(),
  tax_value: z.string(),
  tax_value_cents: z.string(),
  tax_value_decimals: z.string(),
  tax_value_formatted: z.string(),
  gross_value: z.string(),
  gross_value_cents: z.string(),
  gross_value_decimals: z.string(),
  gross_value_formatted: z.string(),
})

export const ResourceDataShapeSchema = z.object({
  alias: z.string().nullable(),
  is_category_default: z.boolean(),
  resource_id: z.number(),
  resource_uuid: z.string(),
  price: z.number(),
  purchase_price: z.number(),
  recurring_fee: z.number(),
  setup_fee: z.number(),
  sales_setup_fee: z.number().optional(),
  sales_recurring_fee: z.number().optional(),
  lowest_purchase_price: z.number().nullable().optional(),
  resource_rate_id: z.number().optional().nullable(), // TODO remove after API is the same on all brands
  lower_limit: z.number(),
  upper_limit: z.number(),
  included_value: z.number(),
})

export const ParentPromotionShapeSchema = z.object({
  parent_plan_id: z.number(),
  price_register: z.number(),
  purchase_price: z.number(),
  tax_rate: z.number(),
  lowest_purchase_price: z.number().nullable().optional(),
})

export const CloudBluePriceShapeSchema = z.object({
  netto: z.number(),
  gross: z.number(),
  tax: z.number(),
})

export const CloudBluePeriodShapeSchema = z.object({
  id: z.number(),
  lowest_purchase_price: z.number().nullable().optional(),
  price_no_promo: z.number(),
  price_renew: z.number(),
  price_register: z.number(),
  purchase_price: z.number(),
  period_name: z.string(),
  default: z.boolean().optional(),
  promoted: z.boolean(),
  tax_rate: z.number(),
  trial: z.boolean().optional(),
  plan_id: z.number(),
  has_promo_limit: z.boolean(),
  first_commitment_price: CloudBluePriceShapeSchema.optional(),
  commitment_count: z.number().optional(),
  commitment_price: CloudBluePriceShapeSchema.optional(),
  commitment_period: CloudBluePeriodValueSchema.optional(),
  renewal_period: CloudBluePeriodValueSchema.nullable().optional(),
  has_multiple_commitments: z.boolean().optional(),
  one_time_fee_item: z.boolean().optional(),
  price_presentation: z.enum(["default", "first_purchase_free"]),
  regularPurchasePrice: BasePriceShapeSchema,
  purchasePrice: BasePriceShapeSchema,
  renewalPrice: BasePriceShapeSchema,
  transferPrice: BasePriceShapeSchema.nullable(),
  resource_data: z.array(ResourceDataShapeSchema),
  parent_promotions: z.array(ParentPromotionShapeSchema),
})

export const FullRemoteProductResourceSchema = z.object({
  id: z.number(),
  uuid: z.string().optional(), // TODO change to required after API is the same on all brands
  setup_fee_changed_per_unit: z.boolean(),
  alias: z.string(),
  sort_order: z.number().optional(),
  name: z.string(),
  description: z.string(),
  measure_unit: z.string(),
  /**
   * @deprecated Nie używać, zostaje usunięte w API 24.04.8 (ONESTORE-123)
   */
  min_value: z.number().optional(), // | never
  /**
   * @deprecated Nie używać, zostaje usunięte w API 24.04.8 (ONESTORE-123)
   */
  max_value: z.number().optional(), // | never
  /**
   * @deprecated Nie używać, zostaje usunięte w API 24.04.8 (ONESTORE-123)
   */
  included_value: z.number().optional(), // | never
})

export const ParametersSchema = z.object({
  paramId: z.string(),
  description: z.string(),
  paramName: z.string(),
  required: z.boolean(),
  value: z.string().nullable(),
  paramType: z.string(),
  paramAlias: z.string().nullable(),
  paramWeight: z.number(),
  defaultValue: z.string(),
})

export const FullRemoteProductPlaceholderSchema = z.object({
  type: z.string(),
  uuid: z.string(),
  plans: z.array(z.number()),
  price: z.number(),
  periodName: z.string(),
  configurationName: z.string(),
})
export const ProductDefinitionResourceSchema = z.object({
  id: z.number(),
  setup_fee_changed_per_unit: z.boolean(),
  alias: z.string().nullable(),
  name: z.string(),
  description: z.string(),
  measure_unit: z.string(),
})
export const ResourcesCategorySchema = z.object({
  id: z.number(),
  name: z.string(),
  resources: z.array(ProductDefinitionResourceSchema).optional(),
  description: z.string(),
  parent: z.number().nullable().optional(),
  default_resource_id: z.number().nullable(),
  default_resource_category: z.null(),
  optional: z.boolean(),
  display_type: z.number(),
  sort_order: z.number(),
})
export const QuantityResourceSchema = z.object({
  name: z.string(),
  measure_unit: z.string(),
  id: z.number(),
  uuid: z.string().optional(), // TODO change to required after API is the same on all brands
  setup_fee_changed_per_unit: z.boolean(),
  /**
   * @deprecated Nie używać, zostaje usunięte w API 24.04.8 (ONESTORE-123)
   */
  min_value: z.number().optional(),
  /**
   * @deprecated Nie używać, zostaje usunięte w API 24.04.8 (ONESTORE-123)
   */
  max_value: z.number().optional(),
  /**
   * @deprecated Nie używać, zostaje usunięte w API 24.04.8 (ONESTORE-123)
   */
  included_value: z.number().optional(),
  alias: z.null(),
  /**
   * @deprecated Nie używać, zostaje usunięte w API 24.04.8 (ONESTORE-123)
   */
  description: z.string(),
})

export const CloudBlueProductSchema = z.object({
  name: z.string(),
  id: z.number(),
  min_quantity: z.number().nullable(),
  max_quantity: z.number().nullable(),
  long_description: z.string(),
  show_resources: z.boolean(),
  has_quantity: z.boolean(),
  promoted: z.boolean(),
  advanced_configuration: z.boolean(),
  definition_type: z.string(), //  z.enum(["vat", "regon"]),
  default_plan_period: z.string(),
  one_time_fee_item: z.boolean(),
  alias: z.string(),
  uuid: z.string(),
  periods: z.array(CloudBluePeriodShapeSchema),
  resources: z.union([
    z.array(FullRemoteProductResourceSchema),
    z.record(z.string(), FullRemoteProductResourceSchema),
  ]),
  placeholders: z.array(FullRemoteProductPlaceholderSchema),
  parameters: z.union([
    z.record(z.string(), ParametersSchema),
    z.array(ParametersSchema),
  ]),
  quantity_resource: QuantityResourceSchema.nullable(),
  resource_categories: z.array(ResourcesCategorySchema),
  description: z.string().optional(),
  priority: z.number().optional(),
  product_type: z.string().optional(),
  metadata: z.any().optional(),
})

export type CloudBlueProductType = z.infer<typeof CloudBlueProductSchema>
export type CloudBluePeriodType = z.infer<typeof CloudBluePeriodShapeSchema>
export type QuantityResourceType = z.infer<typeof QuantityResourceSchema>
export type BasketAttributesType = z.infer<typeof BasketAttributesSchema>

export const CloudBlueProductsResponseSchema = z.array(CloudBlueProductSchema)

export const CreateSessionResponseSchema = z.object({
  hit_id: z.string().nullable().optional(),
  signature: z.string(),
  account_id: z.number(),
  action: z.string().nullable(),
  token: z.string(),
  login: z.string(),
})

export type CreateSessionResponse = z.infer<typeof CreateSessionResponseSchema>

const OrderResponseSchema = z.object({
  redirect_data: z
    .object({
      url: z.string(),
      method: z.enum(["MODAL", "POST", "GET"]),
      data: z.union([z.null(), z.array(z.unknown())]),
    })
    .nullable(),
  bank_transfer_data: z.object({
    title: z.string().nullable(), // TODO dlaczego null
    accountNumber: z.string().nullable(),
    recipient: z.array(z.string()),
  }),
  token: z.string(),
  modal_form: z
    .object({
      checkoutScriptSrc: z.string().nullable(),
      checkoutScriptSection: z.string().nullable(),
      checkoutScriptHandler: z.string().nullable(),
      checkoutScriptHtml: z.string().nullable(),
    })
    .nullable(),
  id: z.number(),
  number: z.string(),
  pay_tool_type: z.union([z.number(), z.string()]).nullable(), // number for gateway tool ids, string (e.g. "MANUAL") for non-gateway orders like bank transfer
  total_value: z.number(),
  total_vat_value: z.number(),
  control_panel_url: z.string().nullable().optional(), // absent (not just null) until the order/service is provisioned
})

const SessionResponseSchema = z.object({
  id: z.string(),
  expire_at: z.string(),
})

export const CheckoutResponseSchema = z.object({
  session: SessionResponseSchema.optional(),
  order: OrderResponseSchema,
  hitId: z.string().nullable().optional(),
  accountId: z.number().nullable().optional(),
})

export type CheckoutResponse = z.infer<typeof CheckoutResponseSchema>
export type OrderResponse = z.infer<typeof OrderResponseSchema>
